import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, ArrowUpDown, X, Search as SearchIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { fmtMoney, fmtNumber } from '@/lib/format';

/*
  Kit común del panel (admin, Dirección/CDM y área de miembros) — 09/10/2026.
  Una sola fuente para tablas, fichas, filtros, insignias, botones y formatos. Colores: tokens de Tailwind
  (gold, navy) definidos en tailwind.config.js. No crear variantes locales de estos componentes.
*/
export const eur = (v) => fmtMoney(Math.round((Number(v) || 0) * 100) / 100, 'eur');
export const money = (v, cur) => (String(cur).toUpperCase() === 'W2USDT' ? `${fmtNumber(v, 2)} W2USDT` : fmtMoney(Math.round((Number(v) || 0) * 100) / 100, cur));
export const num = (v, d = 0) => fmtNumber(Number(v) || 0, d);
export const pct = (v) => `${fmtNumber((Number(v) || 0) * 100, 1)} %`;
export const fdate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
// Fecha de hoy (AAAA-MM-DD) en la zona horaria local, no en UTC
export const todayLocal = () => {
  const x = new Date();
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
// Fecha y hora de un instante (ISO con zona) en la hora local de quien mira
export const fdatetime = (at) => {
  if (!at) return '—';
  const x = new Date(at);
  if (Number.isNaN(x.getTime())) return String(at);
  return `${x.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })} ${x.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
};
// Años para los selectores: los que tienen datos en el CDM (status.years) y siempre el año en curso
export const yearOptions = (cdm) => {
  const now = new Date().getFullYear();
  const ys = new Set([...(cdm?.years || []), now]);
  return [...ys].sort((a, b) => a - b);
};
export const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const inputCls = 'bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40';
export const btnGold = 'inline-flex items-center justify-center gap-2 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50';
export const btnGhost = 'inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm text-white hover:bg-white/[0.05] disabled:opacity-50';

export function useLoad(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    return fn().then(setData).catch((e) => setError(e.message || 'Error al cargar'));
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, error, reload: load, setData };
}

export function Loading({ error }) {
  if (error) return <p className="rounded-xl border border-red-400/20 bg-red-400/[0.05] p-4 text-sm text-red-300" role="alert">{error}</p>;
  return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#c9a84c]" size={24} /></div>;
}

export function Kpi({ label, value, sub, tone }) {
  const color = tone === 'bad' ? 'text-red-300' : tone === 'good' ? 'text-emerald-300' : 'text-white';
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`text-lg font-semibold tabular-nums ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function Block({ title, children, right }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs uppercase tracking-wider text-gray-400 font-semibold">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Badge({ children, tone = 'neutral', title }) {
  const cls = {
    neutral: 'text-gray-300 border-white/10 bg-white/[0.04]',
    gold: 'text-[#c9a84c] border-[#c9a84c]/30 bg-[#c9a84c]/10',
    good: 'text-emerald-300 border-emerald-400/20 bg-emerald-400/10',
    warn: 'text-amber-200 border-amber-400/20 bg-amber-400/10',
    bad: 'text-red-300 border-red-400/20 bg-red-400/10',
    info: 'text-sky-200 border-sky-400/20 bg-sky-400/10',
    violet: 'text-violet-200 border-violet-400/20 bg-violet-400/10',
  }[tone];
  return <span title={title} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{children}</span>;
}

/*
  Tabla simple con orden por columna.
  cols: [{ key, label, render?, sort?: (row) => valor, align?: 'right' }]
*/
export function Table({ cols, rows, empty = 'Sin datos', rowKey = 'id', onRow, max = 500 }) {
  const [sort, setSort] = useState(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = cols.find((x) => x.key === sort.key);
    const get = c?.sort || ((r) => r[sort.key]);
    return [...rows].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      return (va > vb ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1);
    });
  }, [rows, sort, cols]);
  if (!rows.length) return <p className="rounded-xl border border-dashed border-white/[0.08] py-8 text-center text-sm text-gray-400">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-white/[0.06] bg-white/[0.015]">
            {cols.map((c) => (
              <th key={c.key} scope="col" className={`px-3 py-2 font-semibold whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.noSort ? c.label : (
                  <button type="button" className="inline-flex items-center gap-1 hover:text-white" onClick={() => setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === 'desc' ? 'asc' : 'desc' }))}>
                    {c.label}<ArrowUpDown size={11} className="opacity-50" />
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, max).map((r, i) => (
            <tr key={r[rowKey] ?? i} onClick={onRow ? () => onRow(r) : undefined}
              className={`border-b border-white/[0.04] align-top ${onRow ? 'cursor-pointer hover:bg-white/[0.02]' : ''}`}>
              {cols.map((c) => (
                <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.className || 'text-gray-300'}`}>
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {sorted.length > max && <p className="px-3 py-2 text-xs text-gray-400">Mostrando {max} de {sorted.length}. Usa el buscador para acotar.</p>}
    </div>
  );
}

export function Search({ value, onChange, placeholder = 'Buscar…' }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} className={`${inputCls} w-full sm:w-72`} />;
}


/* ── Ampliaciones del kit (admin, 09/10/2026) ───────────────────────────── */

// Lista de datos «etiqueta → valor» para las fichas. items: [[label, value], …]; los valores vacíos se ocultan.
export function FieldList({ items, cols = 2 }) {
  const rows = items.filter(Boolean).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!rows.length) return null;
  return (
    <dl className={`grid gap-x-6 gap-y-3 ${cols === 2 ? 'sm:grid-cols-2' : ''}`}>
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wider text-gray-400">{k}</dt>
          <dd className="text-sm text-white break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// Ficha lateral: panel a la derecha en escritorio, pantalla completa en móvil. Esc o clic fuera cierran.
export function Drawer({ open, onClose, title, subtitle, children, footer, wide = false }) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => panelRef.current?.focus(), 30);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Ficha'}>
      <button type="button" aria-label="Cerrar" onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-[2px] cursor-default" />
      <div ref={panelRef} tabIndex={-1}
        className={`relative h-full w-full ${wide ? 'sm:max-w-3xl' : 'sm:max-w-xl'} bg-navy-panel border-l border-white/[0.08] shadow-2xl flex flex-col outline-none`}>
        <header className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b border-white/[0.06]">
          <div className="min-w-0">
            <h2 className="text-white text-lg font-semibold truncate">{title}</h2>
            {subtitle && <div className="text-sm text-gray-400 mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar ficha" className="w-10 h-10 -mr-2 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05]">
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-6">{children}</div>
        {footer && <footer className="px-5 sm:px-6 py-3 border-t border-white/[0.06] flex flex-wrap gap-2 justify-end">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

// Barra de filtros: buscador + selectores. filters: [{ key, label, value, options: [[value, label], …] }]
export function FilterBar({ search, onSearch, placeholder = 'Buscar por nombre, email o teléfono', filters = [], onFilter, right }) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-2">
      <label className="relative flex-1 min-w-0 lg:max-w-sm">
        <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder} aria-label={placeholder}
          className={`${inputCls} w-full h-10 pl-9`} />
      </label>
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <select key={f.key} value={f.value} onChange={(e) => onFilter(f.key, e.target.value)} aria-label={f.label}
            className={`${inputCls} h-10 pr-8 w-full sm:w-auto max-w-full`}>
            {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        {right}
      </div>
    </div>
  );
}

/*
  Tabla de datos con fila clicable (abre la ficha), orden por columna y paginación.
  En móvil cada fila se muestra como tarjeta con las columnas marcadas como `primary` (título) y `mobile`.
  cols: [{ key, label, render?, sort?, align?, primary?, mobile?, hideSm? }]
*/
export function DataTable({ cols, rows, onRow, rowKey = 'id', empty = 'No hay resultados', pageSize = 50, initialSort = null }) {
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [rows]);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = cols.find((x) => x.key === sort.key);
    const get = c?.sort || ((r) => r[sort.key]);
    return [...rows].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (va === vb) return 0;
      if (va === null || va === undefined || va === '') return 1;
      if (vb === null || vb === undefined || vb === '') return -1;
      return (va > vb ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1);
    });
  }, [rows, sort, cols]);
  if (!rows.length) return <p className="rounded-xl border border-dashed border-white/[0.08] py-10 text-center text-sm text-gray-400">{empty}</p>;
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const view = sorted.slice(page * pageSize, page * pageSize + pageSize);
  const primary = cols.find((c) => c.primary) || cols[0];
  const mobileCols = cols.filter((c) => c.mobile && c !== primary);
  const cell = (c, r) => (c.render ? c.render(r) : (r[c.key] ?? '—'));
  const rowProps = (r) => (onRow ? {
    onClick: () => onRow(r), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRow(r); } },
    role: 'button', tabIndex: 0,
  } : {});
  return (
    <div className="space-y-2">
      {/* Escritorio */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-white/[0.06]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-white/[0.06] bg-white/[0.015]">
              {cols.map((c) => (
                <th key={c.key} scope="col" className={`px-3 py-2.5 font-semibold whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''} ${c.hideSm ? 'hidden xl:table-cell' : ''}`}>
                  {c.noSort ? c.label : (
                    <button type="button" className="inline-flex items-center gap-1 hover:text-white"
                      onClick={() => setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === 'desc' ? 'asc' : 'desc' }))}>
                      {c.label}<ArrowUpDown size={11} className="opacity-50" />
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={r[rowKey] ?? i} {...rowProps(r)}
                className={`border-b border-white/[0.04] last:border-0 ${onRow ? 'cursor-pointer hover:bg-white/[0.03] focus:bg-white/[0.04] outline-none' : ''}`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-2.5 align-middle ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.hideSm ? 'hidden xl:table-cell' : ''} ${c.className || 'text-gray-300'}`}>
                    {cell(c, r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Móvil */}
      <ul className="md:hidden space-y-2">
        {view.map((r, i) => (
          <li key={r[rowKey] ?? i} {...rowProps(r)}
            className={`rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 ${onRow ? 'active:bg-white/[0.05] outline-none' : ''}`}>
            <div className="text-white text-sm font-medium">{cell(primary, r)}</div>
            {mobileCols.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
                {mobileCols.map((c) => <span key={c.key} className="min-w-0">{cell(c, r)}</span>)}
              </div>
            )}
          </li>
        ))}
      </ul>
      <Pager page={page + 1} pages={pages} total={sorted.length} onPage={(p) => setPage(p - 1)} />
    </div>
  );
}

// Paginación (páginas desde 1). La usa DataTable y cualquier lista paginada en el servidor.
export function Pager({ page, pages, total, onPage }) {
  if (!pages || pages <= 1) return null;
  return (
    <div className="flex items-center justify-between text-xs text-gray-400 px-1">
      <span>{total} resultados · página {page} de {pages}</span>
      <div className="flex gap-1">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Página anterior" className={`${btnGhost} h-10 w-10 px-0`}><ChevronLeft size={15} /></button>
        <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Página siguiente" className={`${btnGhost} h-10 w-10 px-0`}><ChevronRight size={15} /></button>
      </div>
    </div>
  );
}

// Inicial en un círculo (listas de personas)
export function Avatar({ name, size = 'md' }) {
  const cls = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  return (
    <span aria-hidden="true" className={`${cls} shrink-0 rounded-full bg-gold/10 border border-gold/20 text-gold font-semibold flex items-center justify-center uppercase`}>
      {String(name || '?').trim().charAt(0) || '?'}
    </span>
  );
}

// Valor con retardo (buscadores: no se pide al servidor en cada tecla)
export function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Pestañas internas (segmentadas). tabs: [[value, label, count?], …]
export function Segmented({ tabs, value, onChange }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-1" role="tablist">
      {tabs.map(([v, l, n]) => (
        <button key={v} type="button" role="tab" aria-selected={value === v} onClick={() => onChange(v)}
          className={`min-h-[36px] px-3 rounded-lg text-sm transition-colors ${value === v ? 'bg-gold text-navy font-semibold' : 'text-gray-300 hover:text-white hover:bg-white/[0.04]'}`}>
          {l}{n !== undefined && <span className={`ml-1.5 text-xs ${value === v ? 'text-navy/70' : 'text-gray-500'}`}>{n}</span>}
        </button>
      ))}
    </div>
  );
}

// Enlaces de contacto rápidos (email, teléfono, WhatsApp)
export function ContactLinks({ email, phone }) {
  const digits = String(phone || '').replace(/\D/g, '');
  const chip = 'inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 h-9 text-xs text-gray-200 hover:border-gold/40 hover:text-white';
  return (
    <div className="flex flex-wrap gap-2">
      {email && <a href={`mailto:${email}`} className={chip}>Email</a>}
      {digits && <a href={`tel:+${digits}`} className={chip}>Llamar</a>}
      {digits && <a href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer" className={chip}>WhatsApp</a>}
    </div>
  );
}
